<script>
import PersonTypeLabel from "@views/admin/treasury/dispersions/components/person-type-label.vue";
import {currency} from "@/filters/currency";
import {fromNowDate, humanizeDate} from "@/filters/momentTzConvert";
import OtpTwoFactorAuthenticator from "@components/Otp/OtpTwoFactorAuthenticator.vue";
import DispersionQueueStatus from "@views/admin/treasury/dispersions/components/dispersion-queue-status.vue";
import {mapActions, mapState} from "vuex";
import {registryAmount} from "@/modules/dispersion/actions";
import {BankAccountStatus} from "@/models/BankAccountStatus";

export default {
  name: "DispersionTable",
  components: {DispersionQueueStatus, OtpTwoFactorAuthenticator, PersonTypeLabel},
  filters: {
    currency,
    humanizeDate
  },
  computed: {
    BankAccountStatus() {
      return BankAccountStatus
    },
    ...mapState("dispersion", ["processors"]),
    dispersionAmountTotal() {
      return this.localDispersion.reduce((acc, cur) => acc + cur.amount, 0);
    },
    localDispersion() {
      return this.dispersions.filter(dispersion => dispersion.hide === false)
          .sort((a, b) => {
            return new Date(a.requestedAt) - new Date(b.requestedAt);
          });
    },
  },
  watch: {
    dispersionAmountTotal() {
      if (this.action === "pending") {
        this.registryAmount({amount: this.dispersionAmountTotal, index: this.identity});
      }
    }
  },
  props: {
    identity: {
      type: String,
      required: true
    },
    action: {
      type: "pending" | "in-process" | "refunded",
      default: "pending"
    },
    expand: {
      type: Boolean,
      default: false
    },
    disperseAction: {
      type: String,
      required: true
    },
    refundAction: {
      type: String,
      required: true
    },
    showProjectInfo: {
      type: Boolean,
      default: false
    },
    showFiscalInfo: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    dispersions: {
      type: Array,
      required: true
    }
  },
  methods: {
    ...mapActions("dispersion", ["registryAmount"]),
    fromNowDate,
    availableTrx(dispersion) {
      return dispersion.trxs.filter(trx => trx.hide === false);
    },
    async returnTrx(trx, dispersion) {
      try {
        await axios.post(this.refundAction, {trxs: [trx]})
        trx.hide = true;
        dispersion.amount -= trx.amount;
        if (dispersion.trxs.filter(trx => trx.hide === false).length === 0) {
          dispersion.hide = true;
        }
        this.$vs.notify({
          title: "Éxito",
          text: "La transacción se ha devuelto correctamente",
          color: "success"
        });
      } catch (e) {
        if (e.response.status === 400) {
          this.$vs.notify({
            title: "Error",
            text: e.response.data.message,
            color: "danger"
          });
        }
        if (e.response.status === 403) {
          this.$vs.notify({
            title: "Error",
            text: "No tienes los permisos suficientes para realizar esta acción",
            color: "danger"
          });
        }
      }
    },
    async returnTrxHandler(trx, dispersion) {
      this.$refs.twoFactorAuthenticator.show();
      this.$refs.twoFactorAuthenticator.$once('confirm', async (value) => {
        if (value === true) {
          this.$refs.twoFactorAuthenticator.close()
          await this.returnTrx(trx, dispersion);
        }
      });
    },
    async refundDispersion(dispersion) {
      let trxPlucked = dispersion.trxs.map((trx) => { return { id: trx.id }; });

      this.$vs.loading();

      try {
        await axios.post(this.refundAction, {trxs: trxPlucked})
        dispersion.hide = true;
        this.$vs.notify({
          title: "Éxito",
          text: "La dispersión se ha devuelto correctamente",
          color: "success"
        });
      }catch (e) {
        if (e.response.status === 400) {
          this.$vs.notify({
            title: "Error",
            text: e.response.data.message,
            color: "danger"
          });
        }
        if (e.response.status === 403) {
          this.$vs.notify({
            title: "Error",
            text: "No tienes los permisos suficientes para realizar esta acción",
            color: "danger"
          });
        }
      } finally {
        this.$vs.loading.close();
      }
    },
    async refundDispersionHandler(dispersion) {
      this.$refs.twoFactorAuthenticator.show();
      this.$refs.twoFactorAuthenticator.$once('confirm', async (value) => {
        if (value === true) {
          this.$refs.twoFactorAuthenticator.close()
          await this.refundDispersion(dispersion);
        }
      });
    },
    async disperseHandler(dispersion) {
      dispersion.jobStatus = "processing";

      try {
        let trxPlucked = dispersion.trxs.map((trx) => {
          return {id: trx.id};
        });
        await axios.post(this.disperseAction, {trxs: trxPlucked, processor: "stp"});
        dispersion.status = 2;
        dispersion.jobStatus = 'done';
      } catch (e) {
        if (e.response.status === 400) {
          dispersion.error = e.response.data.message;
        }
        if (e.response.status === 403) {
          dispersion.error = "No tienes los permisos suficientes para realizar esta acción";
        }
        dispersion.jobStatus = 'error';
      }
    },
    async disperse(dispersion, check2Fa) {
      if (check2Fa) {
        this.$refs.twoFactorAuthenticator.show();
        this.$refs.twoFactorAuthenticator.$once('confirm', async (value) => {
          if (value === true) {
            this.$refs.twoFactorAuthenticator.close()
            await this.disperseHandler(dispersion);
          }
        });
        return;
      }

      await this.disperseHandler(dispersion);
    },
    async bulkDisperse() {
      this.$refs.twoFactorAuthenticator.show();
      this.$refs.twoFactorAuthenticator.$once('confirm', async (value) => {
        if (value === true) {
          this.$refs.twoFactorAuthenticator.close();
          for (let dispersion of this.localDispersion) {
            await this.disperse(dispersion, false);
          }
        }
      });
    },
  }
}
</script>

<template>
  <vs-card class="text-sm">
    <otp-two-factor-authenticator
        ref="twoFactorAuthenticator"></otp-two-factor-authenticator>

    <div slot="header">
      <div class="flex justify-between">
        <h5 class="flex-1">{{ title }}</h5>
        <div class="flex items-center">
          <span class="mr-5">
            <strong>Total: ${{ dispersionAmountTotal|currency }}</strong>
          </span>
          <vs-button
              @click="bulkDisperse"
              v-if="action === 'pending'"
              color="primary"
              size="small"
              :disabled="localDispersion.length === 0"
          >
            Dispersar Todo ({{ localDispersion.length }})
          </vs-button>
        </div>
      </div>
    </div>
    <vs-table stripe :data="localDispersion">
      <template slot="thead">
        <vs-th>TRX</vs-th>
        <vs-th v-if="showProjectInfo">Proyecto</vs-th>
        <vs-th v-if="showFiscalInfo">Razón social</vs-th>
        <vs-th>Tipo P.</vs-th>
        <vs-th>STP</vs-th>
        <vs-th>Banco</vs-th>
        <vs-th>Monto</vs-th>
        <vs-th>F. Solicitud</vs-th>
        <vs-th>Acciones</vs-th>
      </template>

      <template slot-scope="{data}">
        <vs-tr v-for="(dispersion, idx) in data" v-if="!dispersion.hide">
          <vs-td>
            <div class="flex gap-2">
              <dispersion-queue-status :queue="{
                status: dispersion.jobStatus,
                message: dispersion.error
              }"/>

              <div class="text-xs">
                {{ dispersion.type }}
                <span v-if="expand">({{ availableTrx(dispersion).length }})</span>
              </div>
            </div>
          </vs-td>
          <vs-td v-if="showProjectInfo">
            <div class="text-sm">
              <strong>Alias:</strong> {{ dispersion.projectAlias || "NA" }}
            </div>
            <div class="text-xs">
              <strong>PID:</strong> {{ dispersion.projectId }} |
              <strong>{{ dispersion.profilePrefix }}:</strong> {{ dispersion.profileId }} |
              <strong>{{ dispersion.fiscalPrefix }}:</strong> {{ dispersion.fiscalId }}
            </div>
          </vs-td>
          <vs-td v-if="showFiscalInfo">
            {{ dispersion.fiscalName }} <br/>
            <span class="text-xs">
              <strong>{{ dispersion.profilePrefix }}:</strong> {{ dispersion.profileId }} |
              <strong>{{ dispersion.fiscalPrefix }}:</strong> {{ dispersion.fiscalId }}
            </span>
          </vs-td>
          <vs-td>
            <person-type-label :person_type="dispersion.personType"></person-type-label>
          </vs-td>
          <vs-td>
            <div v-if="dispersion.stpAccountDto !== null">
              <vx-tooltip
                  v-if="dispersion.stpAccountDto.status === 'error'"
                  color="danger"
                  :text="`${dispersion.stpAccountDto.stpClabe}: ${dispersion.stpAccountDto.errorDetails}`">
                <em class="fas fa-circle text-danger"></em>
              </vx-tooltip>

              <span class="cursor-pointer" v-else>
                <vx-tooltip :text="dispersion.stpAccountDto.stpClabe">
                  <em class="fas fa-circle" :class="{
                      'text-success' : (dispersion.stpAccountDto.status === 'created'),
                      'text-warning' : (dispersion.stpAccountDto.status === 'in_process')
                  }"></em>
                </vx-tooltip>
              </span>
            </div>
            <div v-else>
              <em class="fas fa-circle"></em>
            </div>
          </vs-td>
          <vs-td>
            <div v-if="dispersion.bankAccountDto !== null">
              <div :class="{'text-danger': dispersion.bankAccountDto.verified !== BankAccountStatus.VERIFIED}">
                <strong>{{ dispersion.bankAccountDto.bank }}</strong> <br/>
                {{ dispersion.bankAccountDto.clabe }}
              </div>
            </div>
          </vs-td>
          <vs-td>${{ dispersion.amount | currency }}</vs-td>
          <vs-td>
            <span class="text-sm">{{ dispersion.requestedAt|humanizeDate }}</span>
          </vs-td>
          <vs-td>
            <div v-if="action === 'pending'">
              <vs-button
                  @click="disperse(dispersion, true)"
                  radius color="dark" class="md-icon-button">D
              </vs-button>
              <vs-button
                  @click="dispersion.hide = true"
                  radius
                  color="secondary"
                  class="md-icon-button text-dark">
                <em class="fa fa-eye-slash text-sm"></em>
              </vs-button>
            </div>
            <div v-if="action === 'refunded'">
              <vs-button
                  size="small"
                  @click="refundDispersionHandler(dispersion, true)"
                  color="danger"
              >
                Devolver
              </vs-button>
            </div>
          </vs-td>
          <template v-if="expand" slot="expand">
            <div class="con-expand-users w-full">
              <vs-table :data="availableTrx(dispersion)">
                <template slot="thead">
                  <vs-th>Id</vs-th>
                  <vs-th>TRX</vs-th>
                  <vs-th>Monto</vs-th>
                  <vs-th>F. verificación</vs-th>
                  <vs-th>Estatus</vs-th>
                  <vs-th>Acciones</vs-th>
                </template>
                <template slot-scope="{data}">
                  <vs-tr v-for="trx in data" v-if="!trx.hide" :key="trx.id">
                    <vs-td>{{ trx.id }}</vs-td>
                    <vs-td>{{ trx.trxType }}</vs-td>
                    <vs-td>{{ trx.amount | currency }}</vs-td>
                    <vs-td>
                      <vx-tooltip :text="fromNowDate(trx.createdAt)">
                        {{ trx.createdAt|humanizeDate }}
                      </vx-tooltip>
                    </vs-td>
                    <vs-td>{{ trx.status }}</vs-td>
                    <vs-td>
                      <vs-button
                          v-if="identity === 'applicant'"
                          @click="returnTrxHandler(trx, dispersion, idx)"
                          size="small"
                          color="danger"
                      >
                        Devolver
                      </vs-button>
                    </vs-td>
                  </vs-tr>
                </template>
              </vs-table>
            </div>
          </template>
        </vs-tr>
      </template>
    </vs-table>
  </vs-card>
</template>

<style scoped>

</style>